import { type HardwareDatadogAttributes } from '@/utils/analytics/log-hardware-datadog-event';
import { ScribeRemoteKey } from '@/utils/analytics/scribe-remote-keys';

export type RemappedObservabilityEvent = {
  eventName: string;
  attributes: HardwareDatadogAttributes;
};

/**
 * Remap legacy Stream live-chunk `chronicle:observability` event names onto
 * `scribe.remote.*` keys. Chunk-live Rust already emits the new names; Stream
 * still uses `hardware_chunk_upload_*` / `native_chunk_upload_*` via
 * `emit_live_chunk_breadcrumb`. Already-`scribe.remote.*` names pass through.
 *
 * Returns `null` for the `native_chunk_upload_*` twin of each Stream
 * breadcrumb: both names carry the same chunk, so forwarding both would
 * double-count `chunk_transferred`.
 */
export function remapScribeRemoteObservabilityEvent(
  eventName: string,
  attributes: HardwareDatadogAttributes
): RemappedObservabilityEvent | null {
  if (eventName.startsWith('scribe.remote.')) {
    return { eventName, attributes };
  }

  switch (eventName) {
    case 'hardware_chunk_upload_started':
      return {
        eventName: ScribeRemoteKey.ChunkQueued,
        attributes: { ...attributes, source: 'stream', lane: 'stream' },
      };
    case 'hardware_chunk_upload_succeeded':
      return {
        eventName: ScribeRemoteKey.ChunkTransferred,
        attributes: { ...attributes, lane: 'stream', result: 'persisted' },
      };
    case 'hardware_chunk_upload_failed':
      return {
        eventName: ScribeRemoteKey.ChunkTransferred,
        attributes: { ...attributes, lane: 'stream', result: 'retryable' },
      };
    case 'native_chunk_upload_success':
    case 'native_chunk_upload_failure':
      return null;
    default:
      return { eventName, attributes };
  }
}
